import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { resolveBrandLogo, handleBrandLogoError } from '../utils/brandLogo';

const AdminSidebar = ({ collapsed, visible, onLogout }) => {
  const location = useLocation();
  const { logoUrl, feviconUrl, restaurantName } = useTheme();
  const { isImpersonating, returnToSuperAdmin } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [clickedMenu, setClickedMenu] = useState(null);
  const [clickedMenuPosition, setClickedMenuPosition] = useState({ top: 0 });
  const [favorites, setFavorites] = useState([]);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');

  // Load user info from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserName(userData.name || '');
        setUserRole(userData.role || '');
      } catch (e) { /* ignore */ }
    }
    if (!userName) setUserName(localStorage.getItem('UserName') || '');
    if (!userRole) setUserRole(localStorage.getItem('UserRole') || '');
  }, []);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('sidebarFavorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
        setFavorites([]);
      }
    }
  }, []);

  // Save favorites to localStorage
  const saveFavorites = (newFavorites) => {
    localStorage.setItem('sidebarFavorites', JSON.stringify(newFavorites));
    setFavorites(newFavorites);
  };

  // Check if a link is favorited
  const isFavorited = (path) => {
    return favorites.some(fav => fav.path === path);
  };

  // Toggle favorite
  const toggleFavorite = (e, path, label, icon, parentIcon = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    const existingIndex = favorites.findIndex(fav => fav.path === path);
    
    if (existingIndex !== -1) {
      // Remove from favorites
      const newFavorites = favorites.filter((_, index) => index !== existingIndex);
      saveFavorites(newFavorites);
    } else {
      // Add to favorites
      const newFavorite = {
        path,
        label,
        icon: parentIcon || icon
      };
      saveFavorites([...favorites, newFavorite]);
    }
  };

  // Define super admin navigation items (merged admin + superadmin)
  const allNavItems = [
    {
      path: '/superadmin/dashboard',
      icon: 'bi bi-speedometer2',
      label: 'Dashboard',
      exact: true
    },
    {
      path: '/superadmin/user-approvals',
      icon: 'bi bi-person-check',
      label: 'User Approvals',
      exact: true
    },
    {
      path: '/superadmin/user-directory',
      icon: 'bi bi-person-lines-fill',
      label: 'User Directory',
      exact: true
    },
    {
      path: '/superadmin/billing',
      icon: 'bi bi-credit-card',
      label: 'Billing',
      submenu: [
        { path: '/superadmin/subscription-plans', label: 'Subscription Plans' },
        { path: '/superadmin/subscriptions', label: 'Subscriptions' },
        { path: '/superadmin/coupons', label: 'Coupons' },
      ]
    },
    {
      path: '/superadmin/tenant-domains',
      icon: 'bi bi-globe2',
      label: 'Tenant Domains',
      exact: true
    }
  ];

  // Admin navigation items (no filtering needed)
  const navItems = allNavItems;

  const toggleMenu = (menuPath) => {
    if (collapsed) return; // Don't expand menus when sidebar is collapsed
    
    setExpandedMenus(prev => {
      // If clicking on already expanded menu, close it
      if (prev[menuPath]) {
        return {};
      }
      // Otherwise, close all menus and open only the clicked one
      return { [menuPath]: true };
    });
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path || location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMenuExpanded = (menuPath) => {
    return expandedMenus[menuPath] || false;
  };

  const hasActiveSubmenu = (submenu) => {
    return submenu && submenu.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  };

  const handleMenuClick = (menuPath, event) => {
    if (collapsed) {
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      setClickedMenuPosition({ top: rect.top });
      
      // Toggle submenu - close if already open, open if closed
      if (clickedMenu === menuPath) {
        setClickedMenu(null);
      } else {
        setClickedMenu(menuPath);
      }
    } else {
      // Normal behavior for expanded sidebar
      toggleMenu(menuPath);
    }
  };

  // Close submenu when clicking outside
  const handleDocumentClick = useCallback((event) => {
    if (collapsed && clickedMenu) {
      const submenu = document.querySelector('.nav-submenu-hover');
      const menuItem = event.target.closest('.nav-menu-group');
      
      if (!submenu?.contains(event.target) && !menuItem) {
        setClickedMenu(null);
      }
    }
  }, [collapsed, clickedMenu]);

  // Add document click listener
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <div className={`dms-sidebar ${collapsed ? 'collapsed' : ''} ${visible ? 'show' : ''}`}>
      {isImpersonating && (
        <button
          type="button"
          className="sidebar-impersonation-btn"
          onClick={returnToSuperAdmin}
          title={collapsed ? 'Back to Super Admin' : ''}
        >
          <i className="bi bi-arrow-left-circle-fill"></i>
          {!collapsed && (
            <span className="sidebar-impersonation-text">
              <span className="sidebar-impersonation-label">Back to Super Admin</span>
              <span className="sidebar-impersonation-sub">Viewing as {restaurantName || localStorage.getItem('UserName') || 'User'}</span>
            </span>
          )}
        </button>
      )}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-logo">
            <img
              src={resolveBrandLogo(logoUrl, feviconUrl)}
              alt={restaurantName || "Restaurant"}
              className="sidebar-logo-img"
              onError={handleBrandLogoError(feviconUrl)}
            />
          </div>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="sidebar-user-card" title={collapsed ? `${userName || 'User'} - ${userRole || 'Admin'}` : ''}>
        <div className="sidebar-user-avatar">
          {(userName || 'A').charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{userName || 'User'}</span>
            <span className="sidebar-user-role">{(userRole || 'Admin').replace(/_/g, ' ')}</span>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="favorites-section">
            {!collapsed && (
              <div className="section-header">
                <i className="bi bi-star-fill"></i>
                
                <div className="menu-label-line"></div>
              </div>
            )}
            <div className="favorites-list">
              {favorites.map((favorite, index) => (
                <div key={index} className="nav-item">
                  <Link
                    to={favorite.path}
                    className={`nav-link ${isActive(favorite.path) ? 'active' : ''}`}
                    title={collapsed ? favorite.label : ''}
                  >
                    <span className="nav-icon-wrap">
                      <i className={favorite.icon}></i>
                    </span>
                    {!collapsed && <span className="nav-label">{favorite.label}</span>}
                    {!collapsed && (
                      <i
                        className="bi bi-star-fill favorite-icon filled"
                        onClick={(e) => toggleFavorite(e, favorite.path, favorite.label, favorite.icon)}
                        title="Remove from favorites"
                      ></i>
                    )}
                  </Link>
                </div>
              ))}
            </div>
            {!collapsed && <div className="section-divider"></div>}
          </div>
        )}

        {/* MENU Section Label */}
        {!collapsed && (
          <div className="sidebar-menu-label">
            <span>MENU</span>
            <div className="menu-label-line"></div>
          </div>
        )}

        {/* Regular Navigation Items */}
        {navItems.map((item, index) => (
          <div key={index} className="nav-item">
            {item.submenu ? (
              <div className="nav-menu-group">
                <div
                  className={`nav-link nav-link-expandable ${
                    isActive(item.path) || hasActiveSubmenu(item.submenu) || (collapsed && clickedMenu === item.path) ? 'active' : ''
                  }`}
                  onClick={(e) => handleMenuClick(item.path, e)}
                  title={collapsed ? item.label : ''}
                >
                  <span className="nav-icon-wrap">
                    <i className={item.icon}></i>
                  </span>
                  {!collapsed && (
                    <>
                      <span className="nav-label">{item.label}</span>
                      <i
                        className={`bi ${isMenuExpanded(item.path) ? 'bi-chevron-down' : 'bi-chevron-right'} nav-chevron`}
                        style={{
                          transition: 'transform 0.3s ease'
                        }}
                      ></i>
                    </>
                  )}
                </div>

                {!collapsed && (
                  <div className={`nav-submenu ${isMenuExpanded(item.path) ? 'expanded' : ''}`}>
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className={`nav-sublink ${isActive(subItem.path) ? 'active' : ''}`}
                      >
                        <i
                          className={`bi ${isFavorited(subItem.path) ? 'bi-star-fill' : 'bi-star'} favorite-icon ${isFavorited(subItem.path) ? 'filled' : ''}`}
                          onClick={(e) => toggleFavorite(e, subItem.path, subItem.label, item.icon)}
                          title={isFavorited(subItem.path) ? 'Remove from favorites' : 'Add to favorites'}
                        ></i>
                        <span className="nav-sublabel">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                {collapsed && clickedMenu === item.path && (
                  <div
                    className="nav-submenu-hover"
                    style={{ top: clickedMenuPosition.top }}
                  >
                    <div className="submenu-header">
                      <span className="submenu-title">{item.label}</span>
                    </div>
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className={`nav-sublink-hover ${isActive(subItem.path) ? 'active' : ''}`}
                        onClick={() => setClickedMenu(null)}
                      >
                        <span className="nav-sublabel">{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                to={item.path}
                className={`nav-link ${isActive(item.path, item.exact) ? 'active' : ''}`}
                title={collapsed ? item.label : ''}
              >
                <span className="nav-icon-wrap">
                  <i className={item.icon}></i>
                </span>
                {!collapsed && <span className="nav-label">{item.label}</span>}
                {!collapsed && (
                  <i
                    className={`bi ${isFavorited(item.path) ? 'bi-star-fill' : 'bi-star'} favorite-icon ${isFavorited(item.path) ? 'filled' : ''}`}
                    onClick={(e) => toggleFavorite(e, item.path, item.label, item.icon)}
                    title={isFavorited(item.path) ? 'Remove from favorites' : 'Add to favorites'}
                  ></i>
                )}
              </Link>
            )}
          </div>
        ))}
      </nav>

      <a
        href="https://webdekho.in"
        target="_blank"
        rel="noopener noreferrer"
        className="managed-by-sidebar"
        title="Webdekho"
      >
        <img src="https://webdekho.in/images/wpl_rms.png" alt="Webdekho" className="managed-by-logo" />
      </a>

      <div className="sidebar-logout">
        <button
          className="sidebar-signout-btn"
          onClick={onLogout}
          title={collapsed ? 'Sign Out' : ''}
        >
          <i className="bi bi-box-arrow-right"></i>
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
